// * Importing necessary modules
import { useState, useCallback } from "react";

// ? React Query for data fetching and caching
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// ? API functions for Reviewers
import { reviewersAPI } from "./reviewerAPI";

// ? Types for pagination
import { type Pagination } from "../../../services/stores/types";

// ? Auxiliary types for relationships
import { type ReviewerDetails, type CreateReviewerInput } from "./types/types";

/**
 * ? Custom hook `useReviewers` to manage operations related to Reviewers
 * within the context of a specific application.
 *
 * @param {string} applicationId - ID of the application for context-specific operations.
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns reviewers data, loading state, error messages, and functions to interact with the API.
 */
export const useReviewers = (applicationId: string, token: string) => {
  const queryClient = useQueryClient();

  // * States for search and pagination
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define the query key
  const queryKey = [
    "reviewer",
    applicationId,
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Fetch reviewers using react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      reviewersAPI.getAll(
        applicationId,
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000, // * Cache for 5 minutes
    enabled: !!applicationId && !!token, // * Only enable the query if applicationId and token exist
  });

  // * Extract data and pagination
  const reviewers = data?.reviewers || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers for search and pagination
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations to create, update, and delete reviewers
  const createReviewerMutation = useMutation({
    mutationFn: (reviewerData: CreateReviewerInput) =>
      reviewersAPI.create(applicationId, reviewerData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateReviewerMutation = useMutation({
    mutationFn: ({ id, teacher_id }: { id: string; teacher_id: string }) =>
      reviewersAPI.update(applicationId, id, { teacher_id }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteReviewerMutation = useMutation({
    mutationFn: (id: string) => reviewersAPI.delete(applicationId, id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // TODO: Fetch details of a reviewer by ID
  const getReviewerById = useCallback(
    async (id: string): Promise<ReviewerDetails | null> => {
      try {
        const reviewerDetails = await reviewersAPI.getById(
          applicationId,
          id,
          token
        );
        return reviewerDetails;
      } catch (err) {
        console.error("Error fetching application reviewer by ID:", err);
        return null;
      }
    },
    [applicationId, token]
  );

  // * Return values and functions
  return {
    reviewers,
    loading: isLoading || isFetching,
    error: isError ? (error as Error).message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createReviewer: createReviewerMutation.mutate,
    updateReviewer: updateReviewerMutation.mutate,
    deleteReviewer: deleteReviewerMutation.mutate,
    getReviewerById,
    changePage,
    isCreating: createReviewerMutation.isPending,
    isUpdating: updateReviewerMutation.isPending,
    isDeleting: deleteReviewerMutation.isPending,
  };
};
